import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AddSupervisorEvaluationMsg,
  RemoveSupervisorEvaluationMsg,
  SCORING_WORKER_HOST_ACTION,
  UpdateSupervisorEvaluationMsg,
} from '@/workers/scoring/types';
import { ScoringContext } from '@/contexts/scoring/context';
import { COMMON_STRING, SUCCESS_STRING } from '@/constants/dictionary';
import { API_METHOD } from '@/service/constants';
import { FixtureComparisonRating } from '@/service/types';
import { useSupervisorEvaluation } from '@/service/hooks/useSupervisorEvaluation';
import { ComparisonBox } from '../ComparisonRating/common';
import { SupervisorEvaluationButtons } from './SupervisorEvaluationButtons';
import { EvaluationTypes, initialEvaluation } from './constants';
import { SupervisorEvaluationComment } from './SupervisorEvaluationComment';
import { SupervisorEvaluationSelect } from './SupervisorEvaluationSelect';
import { getSupervisorFullName } from './utils/utils';

export interface SupervisorEvaluationProps {
  supervisor?: FixtureComparisonRating;
}

export const SupervisorEvaluation = ({
  supervisor,
}: SupervisorEvaluationProps) => {
  const { supervisorRating, isLoading, mutate } = useSupervisorEvaluation();
  const {
    state: { fixtureId, collectionId },
    useDispatchWithResponse,
  } = useContext(ScoringContext);

  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);

  const supervisorEvaluationInitial: EvaluationTypes = useMemo(() => {
    if (!supervisorRating?.length) return initialEvaluation;

    const supervisorRated = supervisorRating.find(
      (ev) => ev.collectionId === collectionId,
    );
    if (supervisorRated) {
      return {
        feedbackText: supervisorRated.feedbackText,
        feedbackType: supervisorRated.feedbackType,
      };
    } else {
      return initialEvaluation;
    }
  }, [collectionId, supervisorRating]);

  const [evaluation, setEvaluation] =
    useState<EvaluationTypes>(initialEvaluation);

  useEffect(() => {
    if (supervisorEvaluationInitial) {
      setEvaluation(supervisorEvaluationInitial);
    }
  }, [supervisorEvaluationInitial]);

  const { dispatch: addSupervisorEvaluation } =
    useDispatchWithResponse<AddSupervisorEvaluationMsg>(
      SCORING_WORKER_HOST_ACTION.ADD_SUPERVISOR_EVALUATION,
    );

  const { dispatch: updateSupervisorEvaluation } =
    useDispatchWithResponse<UpdateSupervisorEvaluationMsg>(
      SCORING_WORKER_HOST_ACTION.UPDATE_SUPERVISOR_EVALUATION,
    );

  const { dispatch: removeSupervisorEvaluation } =
    useDispatchWithResponse<RemoveSupervisorEvaluationMsg>(
      SCORING_WORKER_HOST_ACTION.REMOVE_SUPERVISOR_EVALUATION,
    );

  const onSubmitEvaluation = async (
    action: API_METHOD.POST | API_METHOD.PUT | API_METHOD.DELETE,
  ) => {
    const defaultRequestData = {
      fixtureId,
      collectionId,
      requestTime: new Date().toISOString(),
      supervisorId: supervisor?.user.oktaId || '',
    };

    if (action === API_METHOD.DELETE) {
      await removeSupervisorEvaluation(defaultRequestData, {
        successMessage:
          SUCCESS_STRING.SUPERVISOR_EVALUATION_SUCCESSFULLY_DELETED,
      });
      await mutate();
      setIsOpenDeleteConfirmation(false);
    } else {
      const dataToAdd = {
        ...defaultRequestData,
        feedbackText: evaluation.feedbackText,
        feedbackType: evaluation.feedbackType,
      };

      if (action === API_METHOD.POST) {
        await addSupervisorEvaluation(dataToAdd, {
          successMessage:
            SUCCESS_STRING.SUPERVISOR_EVALUATION_SUCCESSFULLY_ADDED,
        });
        await mutate();
      } else if (action === API_METHOD.PUT) {
        await updateSupervisorEvaluation(dataToAdd, {
          successMessage:
            SUCCESS_STRING.SUPERVISOR_EVALUATION_SUCCESSFULLY_UPDATED,
        });
        await mutate();
      }
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        width: 555,
        px: 4,
        pt: 2,
      }}
    >
      <Paper elevation={1}>
        <Stack position='relative'>
          <ComparisonBox direction='row'>
            <Typography>{COMMON_STRING.SUPERVISOR}:</Typography>
            <Typography fontWeight='bold'>
              {getSupervisorFullName(supervisor)}
            </Typography>
          </ComparisonBox>
          <ComparisonBox>
            <SupervisorEvaluationSelect
              setEvaluation={setEvaluation}
              evaluation={evaluation}
            />
          </ComparisonBox>
          {evaluation.feedbackType && (
            <SupervisorEvaluationComment
              setEvaluation={setEvaluation}
              evaluation={evaluation}
            />
          )}
          <SupervisorEvaluationButtons
            newEvaluation={evaluation}
            onSubmit={onSubmitEvaluation}
            setEvaluation={setEvaluation}
            initialEvaluation={supervisorEvaluationInitial}
            isOpenDeleteConfirmation={isOpenDeleteConfirmation}
            setIsOpenDeleteConfirmation={setIsOpenDeleteConfirmation}
          />
        </Stack>
      </Paper>
    </Box>
  );
};
